import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Table } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getRequestStates } from "../../redux/actions/requestStateActions";
import { formatDate, MOMENT_FORMAT_DATE_DMYHM_DOTS } from "../../functions/formatters";
import StatusChangeDialog from "./StatusChangeDialog";
import { getStateChipColor, getStateLabel } from "./statusHelpers";

function buildSearchFilter({ searchText, stateFilter }) {
    const filter = {};
    const trimmed = searchText?.trim();

    if (trimmed) {
        const asNumber = parseInt(trimmed, 10);
        if (!isNaN(asNumber) && String(asNumber) === trimmed) {
            filter.id = { operator: "equals", operand1: asNumber };
        } else {
            filter.number = { operator: "like", operand1: `%${trimmed}%` };
        }
    }

    if (stateFilter) {
        filter.stateId = { operator: "equals", operand1: Number(stateFilter) };
    }

    return filter;
}

export default function EntityStatusManager({ entity }) {
    const { t } = useTranslation();
    const { push } = useHistory();
    const dispatch = useDispatch();
    const { requestStates } = useSelector((state) => state.requestState);

    const [searchText, setSearchText] = useState("");
    const [stateFilter, setStateFilter] = useState("");
    const [appliedFilter, setAppliedFilter] = useState({ searchText: "", stateFilter: "" });
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [selectedRow, setSelectedRow] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const pageSizeOptions = [10, 15, 25, 50];

    useEffect(() => {
        dispatch(getRequestStates({ paging: { skip: 0, take: 100 } }));
    }, [dispatch]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const filter = buildSearchFilter(appliedFilter);
            const response = await entity.crud.search({
                paging: {
                    skip: (page - 1) * pageSize,
                    take: pageSize,
                    returnCount: true,
                },
                filter,
                sort: { id: { operator: "desc", ordinal: 0 } },
            });
            setRows(response?.result || []);
            setTotal(response?.total ?? response?.count ?? response?.result?.length ?? 0);
        } catch (e) {
            setLoadError(e?.message || String(e));
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [entity.crud, page, pageSize, appliedFilter]);

    useEffect(() => {
        fetchData();
    }, [page, pageSize, appliedFilter, fetchData]);

    const handleSearch = () => {
        setAppliedFilter({ searchText, stateFilter });
        setPage(1);
    };

    const handleReset = () => {
        setSearchText("");
        setStateFilter("");
        setAppliedFilter({ searchText: "", stateFilter: "" });
        setPage(1);
    };

    const handleStatusChange = async (row, oldStateId, newStateId, comment) => {
        await entity.crud.workflowRun(row.id, oldStateId, newStateId, comment || undefined);
        await fetchData();
    };

    const columns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
        },
        {
            title: "Номер",
            dataIndex: "number",
            key: "number",
            width: 140,
            render: (val) => val || "—",
        },
        {
            title: "Дата создания",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 160,
            render: (val) => (val ? formatDate(val, MOMENT_FORMAT_DATE_DMYHM_DOTS) : "—"),
        },
        {
            title: "Статус",
            dataIndex: "state",
            key: "state",
            width: 180,
            render: (state) => (
                <Chip
                    label={getStateLabel(state, t)}
                    color={getStateChipColor(state?.code)}
                    size="small"
                />
            ),
        },
        {
            title: "Доп. информация",
            key: "subtitle",
            ellipsis: true,
            render: (_, row) => entity.subtitle?.(row) || "—",
        },
        {
            title: "Действия",
            key: "actions",
            width: 220,
            fixed: "right",
            render: (_, row) => (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => {
                            setSelectedRow(row);
                            setDialogOpen(true);
                        }}
                    >
                        Статус
                    </Button>
                    {entity.detailRoute && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNewIcon />}
                            onClick={() => push(entity.detailRoute(row.id))}
                        >
                            Открыть
                        </Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {entity.description}
            </Typography>

            <Box
                sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: "grey.50",
                    border: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Поиск по ID или номеру"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Фильтр по статусу</InputLabel>
                            <Select
                                value={stateFilter}
                                label="Фильтр по статусу"
                                onChange={(e) => setStateFilter(e.target.value)}
                            >
                                <MenuItem value="">Все статусы</MenuItem>
                                {requestStates.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.name || s.code}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3} sx={{ display: "flex", gap: 1 }}>
                        <Button
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            fullWidth
                        >
                            Найти
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleReset}
                        >
                            Сброс
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {loadError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {loadError}
                </Alert>
            )}

            <Box sx={{ position: "relative" }}>
                {loading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgba(255,255,255,0.6)",
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={rows}
                    scroll={{ x: 900 }}
                    pagination={{
                        current: page,
                        pageSize,
                        total,
                        showSizeChanger: true,
                        pageSizeOptions,
                        showTotal: (count) => `Всего: ${count}`,
                        onChange: (p, ps) => {
                            setPage(p);
                            setPageSize(ps);
                        },
                    }}
                />
            </Box>

            <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                <Button startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
                    Обновить
                </Button>
            </Box>

            <StatusChangeDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                row={selectedRow}
                entityLabel={entity.label}
                requestStates={requestStates}
                onConfirm={handleStatusChange}
            />
        </Box>
    );
}
